import { Spacer, Text } from "@hackthenorth/north";
import {
  DocumentPlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Button } from "north.js";
import React, { useState } from "react";
import styled from "styled-components";

import { Modal } from "src/shared/components";
import { EditIcon, EyeIcon } from "src/static/icons";
import { mediaQueries } from "src/theme/deviceBreakpoints";
import { Quest, QuestType } from "src/views/goose-games/types";

import FooterActions from "../../../../footerActions";
import InputQuestView from "../../../hacker/home/quest/view/input";
import ScannedQuestView from "../../../hacker/home/quest/view/scanned";
import { MODAL_FIELDS } from "../constants";
import DeleteModal from "../delete";
import { parseQuestMeta, QuestMeta } from "../metadata";
import MetadataInput from "../MetadataInput";

export interface EditModalProps {
  quest: Quest;
  isOpen: boolean;
  onDelete: (id: number, successMessage: string, errorMessage: string) => void;
  onConfirm: (
    id: number,
    successMessage: string,
    errorMessage: string,
    flash: boolean,
    hidden: boolean,
    title?: string,
    type?: QuestType,
    pointsRewarded?: number,
    completionLimit?: number,
    eventCategory?: string,
    description?: string,
    questionString?: string,
    questionAnswer?: string,
    meta?: QuestMeta
  ) => void;
  onCancel: () => void;
}
const EditModal: React.FC<EditModalProps> = ({
  quest,
  isOpen,
  onDelete,
  onConfirm,
  onCancel,
}) => {
  const field = MODAL_FIELDS["QUEST"].edit;
  const [title, setTitle] = useState(quest.title);
  const [questType, setQuestType] = useState<QuestType>(quest.type);
  const [limit, setLimit] = useState<number>(quest.completion_limit);
  const [points, setPoints] = useState<string>(quest.points_rewarded + "");
  const [description, setDescription] = useState<string | undefined>(
    quest.description
  );
  const [answer, setAnswer] = useState<string | undefined>(
    quest.question_answer
  );
  const [input, setInput] = useState(""); //Used only for preview
  const [isFlash, setIsFlash] = useState<boolean>(quest.flash);
  const [isHidden, setIsHidden] = useState<boolean>(quest.hidden);
  const initialMetadata =
    quest.meta == null ? "" : JSON.stringify(quest.meta, null, 2);
  const [metadata, setMetadata] = useState(initialMetadata);
  const parsedMeta = parseQuestMeta(metadata);

  const [showPreview, setShowPreview] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const normalizedTitle = title.trim();
  const normalizedDescription = description?.trim() || undefined;
  const normalizedAnswer = answer?.trim() || undefined;
  const pointsValue = Number(points);
  const isQuestionQuest = questType === QuestType.Question;
  const isMultiRedeemable =
    questType !== QuestType.Question &&
    questType !== QuestType.OrganizerApproved;

  const isFormValid =
    normalizedTitle.length > 0 &&
    Number.isInteger(limit) &&
    limit > 0 &&
    Number.isFinite(pointsValue) &&
    pointsValue > 0 &&
    (!isQuestionQuest || normalizedAnswer !== undefined);

  const hasChangedValues =
    normalizedTitle !== quest.title.trim() ||
    questType !== quest.type ||
    limit !== quest.completion_limit ||
    pointsValue !== quest.points_rewarded ||
    normalizedDescription !== (quest.description?.trim() || undefined) ||
    (isQuestionQuest &&
      normalizedAnswer !== (quest.question_answer?.trim() || undefined)) ||
    isFlash !== quest.flash ||
    isHidden !== quest.hidden ||
    metadata !== initialMetadata;

  const canSubmit = isFormValid && !parsedMeta.error && hasChangedValues;

  const updateQuestType = (type: QuestType) => {
    setQuestType(type);

    if (type === QuestType.Question || type === QuestType.OrganizerApproved) {
      setLimit(1);
    }
  };

  const handleConfirmation = () => {
    if (canSubmit) {
      onConfirm(
        quest.id,
        field.success,
        field.error,
        isFlash,
        isHidden,
        normalizedTitle,
        questType,
        pointsValue,
        limit,
        quest.quest_event_activity_category,
        normalizedDescription,
        isQuestionQuest ? normalizedTitle : undefined,
        isQuestionQuest ? normalizedAnswer : undefined,
        parsedMeta.value
      );
    }
  };

  return (
    <Modal isOpen={isOpen} width="600px">
      {showPreview ? (
        <>
          {showDeleteConfirmation && (
            <DeleteModal
              isOpen={showDeleteConfirmation}
              onConfirm={() =>
                onDelete(
                  quest.id,
                  MODAL_FIELDS["QUEST"].delete.success,
                  MODAL_FIELDS["QUEST"].delete.error
                )
              }
              onCancel={() => setShowDeleteConfirmation(false)}
              type="QUEST"
            />
          )}
          <ModalWrapper>
            <PreviewHeaderContainer>
              <Text mods="h3 bold">Quest preview</Text>
              <ActionIconsContainer>
                <Button
                  aria-label="Edit quest"
                  color="tertiary"
                  leadingIcon={<EditIcon width={18} height={18} />}
                  size="sm"
                  onClick={() => setShowPreview(false)}
                />
                <Button
                  color="tertiary"
                  leadingIcon={<TrashIcon width={20} height={20} />}
                  size="sm"
                  onClick={() => setShowDeleteConfirmation(true)}
                />
                <Button
                  color="tertiary"
                  leadingIcon={<XMarkIcon width={20} height={20} />}
                  size="sm"
                  onClick={() => onCancel()}
                />
              </ActionIconsContainer>
            </PreviewHeaderContainer>
            {quest.type === QuestType.Question ? (
              <InputQuestView
                preview
                quest={quest}
                answer={input}
                updateAnswer={(e) => setInput(e)}
              />
            ) : (
              <ScannedQuestView preview quest={quest} />
            )}
          </ModalWrapper>
        </>
      ) : (
        <>
          <HeaderContainer>
            <IconContainer>
              <HeaderIcon>
                <DocumentPlusIcon />
              </HeaderIcon>
              <Text mods="h3 bold">{field.title}</Text>
            </IconContainer>
            <Button
              color="tertiary"
              leadingIcon={<EyeIcon width={18} height={18} />}
              size="md"
              onClick={() => setShowPreview(true)}
            >
              Preview
            </Button>
          </HeaderContainer>
          <FormContainer>
            <FirstLayer>
              <NameWrapper>
                <Text mods="body bold">
                  {field.name[questType !== QuestType.Question ? 0 : 1]}
                </Text>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={field.name_placeholder}
                  style={{ width: "100%", padding: "8px", borderRadius: "4px" }}
                />
              </NameWrapper>
              <DropdownWrapper>
                <Text mods="body bold">{field.type}</Text>
                {/* React's change event is keyboard and screen-reader accessible. */}
                {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                <select
                  value={questType}
                  onChange={(e) => updateQuestType(e.target.value as QuestType)}
                  style={{ width: "100%", padding: "8px", borderRadius: "4px" }}
                >
                  {quest.type === QuestType.Scanned && (
                    <option value={QuestType.Scanned}>Scanned</option>
                  )}
                  {(
                    field.type_options as Array<{
                      label: string;
                      value: string;
                    }>
                  ).map((opt, i) => (
                    <option key={i} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </DropdownWrapper>
            </FirstLayer>

            <SecondLayer>
              <TogglesRow>
                <ToggleWrapper>
                  <Text mods="body bold">
                    Feature as &quot;Flash&quot; Quest
                  </Text>
                  <ToggleTrack
                    type="button"
                    role="switch"
                    aria-checked={isFlash}
                    aria-label="Toggle flash quest"
                    isOn={isFlash}
                    onClick={() => setIsFlash((prev) => !prev)}
                  >
                    <ToggleKnob isOn={isFlash} />
                  </ToggleTrack>
                </ToggleWrapper>
                <ToggleWrapper>
                  <Text mods="body bold">Hide Quest from Hackers</Text>
                  <ToggleTrack
                    type="button"
                    role="switch"
                    aria-checked={isHidden}
                    aria-label="Hide quest"
                    isOn={isHidden}
                    onClick={() => setIsHidden((prev) => !prev)}
                  >
                    <ToggleKnob isOn={isHidden} />
                  </ToggleTrack>
                </ToggleWrapper>
              </TogglesRow>

              <DescriptionWrapper>
                <Text mods="body bold">Description</Text>
                <DescriptionTextarea
                  placeholder="Give a short 1-2 sentence description..."
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </DescriptionWrapper>

              {questType === QuestType.Question && (
                <DescriptionWrapper>
                  <Text mods="body bold">Answer</Text>
                  <DescriptionTextarea
                    placeholder="Enter the answer..."
                    rows={4}
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                  />
                </DescriptionWrapper>
              )}
            </SecondLayer>

            <ThirdLayer>
              {isMultiRedeemable && (
                <CounterWrapper>
                  <Text mods="body bold">Maximum redeemable times</Text>
                  <CounterContainer>
                    <CounterButton
                      onClick={() =>
                        setLimit((prev) => (prev > 1 ? prev - 1 : prev))
                      }
                    >
                      −
                    </CounterButton>
                    <CounterValue>{limit}</CounterValue>
                    <CounterButton onClick={() => setLimit((prev) => prev + 1)}>
                      +
                    </CounterButton>
                  </CounterContainer>
                </CounterWrapper>
              )}
              <NumberWrapper>
                <Text mods="body bold">{field.num_input}</Text>
                <input
                  type="number"
                  min="1"
                  value={points}
                  onChange={(e) => setPoints(e.target.value)}
                  placeholder={field.num_input_placeholder}
                  style={{ width: "100%", padding: "8px", borderRadius: "4px" }}
                />
              </NumberWrapper>
            </ThirdLayer>
            <MetadataInput
              value={metadata}
              onChange={setMetadata}
              error={parsedMeta.error}
            />
          </FormContainer>
          <Spacer height={36} />
          <FooterActions
            confirmText={"Save Changes"}
            onConfirm={handleConfirmation}
            onCancel={onCancel}
            isConfirmDisabled={!canSubmit}
          />
        </>
      )}
    </Modal>
  );
};

export default EditModal;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 24px;
  justify-content: space-between;
`;

const PreviewHeaderContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-block: 24px;
  padding-inline: 36px;
  min-width: 100%;
  background-color: ${({ theme }) =>
    theme.globalConstants.color.backgroundGray};
  border-bottom: ${({ theme }) =>
    `1px solid ${theme.globalConstants.color.borderGray}`};
`;

const ModalWrapper = styled.div`
  padding-top: 64px;
`;

const ActionIconsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderIcon = styled.div<{ backgroundColor?: string }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: 50%;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ToggleWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const TogglesRow = styled.div`
  display: flex;
  gap: 20px;
`;

const ToggleTrack = styled.button<{ isOn: boolean }>`
  position: relative;
  width: 52px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 14px;
  background: ${({ theme, isOn }) =>
    isOn
      ? theme.globalConstants.color.borderPrimary
      : theme.globalConstants.color.borderGray};
  cursor: pointer;
  transition: background-color 0.2s ease;
`;

const ToggleKnob = styled.div<{ isOn: boolean }>`
  position: absolute;
  top: 3px;
  left: ${({ isOn }) => (isOn ? "27px" : "3px")};
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: left 0.2s ease;
`;

const FirstLayer = styled.div`
  display: flex;
  gap: 20px;

  ${mediaQueries.largeMobile} {
    flex-direction: column;
  }
`;

const NameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const DropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const SecondLayer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${mediaQueries.largeMobile} {
    flex-direction: column;
  }
`;

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DescriptionTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;

  &::placeholder {
    color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.globalConstants.color.borderPrimary};
  }
`;

const ThirdLayer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;

  ${mediaQueries.largeMobile} {
    flex-direction: column;
  }
`;

const CounterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const CounterContainer = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  overflow: hidden;
`;

const CounterButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 18px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};

  &:hover {
    background-color: ${({ theme }) =>
      theme.globalConstants.color.backgroundGray};
  }
`;

const CounterValue = styled.div`
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderGray};
  border-right: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderGray};
  background-color: ${({ theme }) =>
    theme.globalConstants.color.backgroundLight};
`;

const NumberWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;
